<template>
    <div class="tab-pane active"><iframe ref="frame" :src="url" class="external-frame"></iframe></div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import { ROUTE_NAMES } from '../../constants';

export default {
    computed: {
        ...mapState('externals', ['configuration', 'isLoaded']),
        ...mapState({
            token: state => state.user.user.token,
        }),
        /**
         * Gets ID of external page configuration
         * @returns {number}
         */
        id() {
            return parseInt(this.$route.params.id);
        },

        /**
         * Gets external page configuration object (from external.json)
         * @returns {object}
         */
        config() {
            return this.isLoaded && this.configuration[this.id] ? this.configuration[this.id] : {};
        },

        /**
         * Gets external page URL (for the iframe src attribute)
         * @returns {string}
         */
        url() {
            return this.config.url || '';
        },

        /**
         * Gets external page URL origin (part without path and request parameters)
         * @returns {string}
         */
        messageOrigin() {
            const parser = document.createElement('a');
            parser.href = this.url;

            // Fix for IE incorrect behaviour
            /* eslint-disable  no-self-assign */
            if (parser.host === '') {
                parser.href = parser.href;
            }
            /* eslint-enable no-self-assign */

            return parser.protocol + '//' + parser.host;
        },

        /**
         * Gets title for the external page
         * @returns {string}
         */
        title() {
            return this.config.title || this.$t('external.title');
        },
    },

    watch: {
        isLoaded(isLoadedVal) {
            this.setLoading(!isLoadedVal);
            this.checkExternalFound(isLoadedVal);
        },

        id(val, oldVal) {
            if (val !== oldVal) {
                if (this.checkExternalFound(this.isLoaded)) {
                    this.setTitle(this.title);
                }
            }
        },
    },

    mounted() {
        this.checkExternalFound(this.isLoaded);
        window.addEventListener('message', this.handleFrameRequest);
        this.setTitle(this.title);
    },

    beforeDestroy() {
        window.removeEventListener('message', this.handleFrameRequest);
    },

    methods: {
        ...mapMutations({
            setTitle: 'header/SET_EXTERNAL_TITLE',
            setUnsavedChanges: 'SET_UNSAVED_CHANGES',
            setLoading: 'SHOW_LOADING',
        }),
        ...mapActions('confirm', ['confirmation']),

        /**
         * Handles API requests from external page
         * @param {MessageEvent} event
         */
        handleFrameRequest(event) {
            if (!this.config.api) {
                // Access to API is disabled in configuration file
                return;
            }

            if (event.origin !== this.messageOrigin) {
                // Invalid request origin
                return;
            }

            if (!event.data.type) {
                // No request type
                return;
            }

            this.processRequest(event.data);
        },

        /**
         * Performs action on incoming API request from external page
         * @param {object} message
         */
        processRequest(message) {
            switch (message.type) {
                case 'GET_AUTH':
                    this.sendResponse('GET_AUTH', message.id, this.token);
                    break;
                case 'SET_TITLE':
                    this.setTitle('' + message.payload);
                    break;
                case 'NOTIFY_UNSAVED':
                    this.setUnsavedChanges(!!message.payload);
                    break;
                case 'ASK_CONFIRMATION':
                    this.askConfirmation(message);
                    break;
                default:
                // Unsupported request type
            }
        },

        /**
         * Asks confirmation based on appropriate API request
         * @param {object} message
         */
        askConfirmation(message) {
            let { title, body, ok, cancel } = message.payload || {};
            if (!body) {
                // Invalid confirmation request
                return;
            }
            title = title || this.$t('common.confirmTitle');
            ok = ok || this.$t('common.ok');
            cancel = cancel || this.$t('common.cancel');

            this.confirmation({
                confirmTitle: '' + title,
                confirmBody: '' + body,
                confirmOk: '' + ok,
                confirmCancel: '' + cancel,
            })
                .then(result => {
                    this.sendResponse('ASK_CONFIRMATION', message.id, result);
                })
                .catch(() => {
                    this.sendResponse('ASK_CONFIRMATION', message.id, false);
                });
        },

        /**
         * Sends response for API request to external page iframe
         * @param {string} type
         * @param {number} id
         * @param payload
         */
        sendResponse(type, id, payload) {
            this.$refs.frame.contentWindow.postMessage({ type, id, payload }, '*');
        },

        /**
         * Checks if external pages configuration is loaded and current page is in it.
         * Otherwise redirects user to 404 (not found) error page.
         * @param {boolean} isLoadedVal
         * @returns {boolean}
         */
        checkExternalFound(isLoadedVal) {
            if (isLoadedVal && !this.configuration[this.id]) {
                this.$router.push({ name: ROUTE_NAMES.ERROR_404 });
                return false;
            }

            return true;
        },
    },
};
</script>

<style scoped>
.tab-pane {
    overflow: hidden;
}

.external-frame {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    position: fixed;
    top: 0;
    bottom: 0;
    z-index: 38;
    background: white;
}

@media print {
    .external-frame {
        position: static;
        height: auto;
    }
}
</style>
